import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, GraduationCap, Trash2, PenLine } from "lucide-react";

export default function Thesis() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title:"", template_key:"PhD (Traditional)", university:"", supervisor:"" });
  const nav = useNavigate();

  const load = () => api.get("/thesis").then(r => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); api.get("/thesis/templates").then(r => setTemplates(r.data.templates || {})); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Working title required");
    const { data } = await api.post("/thesis", form);
    toast.success("Thesis created"); setOpen(false); nav(`/app/thesis/${data.id}`);
  };
  const del = async (id) => { if (!window.confirm("Delete this thesis?")) return; await api.delete(`/thesis/${id}`); load(); };

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <div className="overline">Doctoral</div>
          <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><GraduationCap className="w-10 h-10 text-primary"/>PhD Thesis</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Write your thesis with confidence — traditional or publication-based formats, AI drafting per chapter, plagiarism &amp; grammar checks, and spin-off papers from your work.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="btn-new-thesis"><Plus className="w-4 h-4 mr-2"/>New thesis</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">Start a new thesis</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Working title</Label><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} placeholder="e.g., Explainable AI for Radiology Triage" data-testid="thesis-new-title"/></div>
              <div><Label>Format</Label>
                <Select value={form.template_key} onValueChange={(v)=>setForm({...form, template_key:v})}>
                  <SelectTrigger data-testid="thesis-new-template"><SelectValue/></SelectTrigger>
                  <SelectContent>{Object.keys(templates).map(k=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>University</Label><Input value={form.university} onChange={(e)=>setForm({...form, university:e.target.value})}/></div>
                <div><Label>Supervisor</Label><Input value={form.supervisor} onChange={(e)=>setForm({...form, supervisor:e.target.value})}/></div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="thesis-new-create">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="text-muted-foreground">Loading…</div> :
       items.length === 0 ? (
         <div className="card-crisp p-10 rounded-lg text-center">
           <div className="font-display text-3xl">Your first chapter awaits</div>
           <p className="text-muted-foreground mt-2">Choose a format and let the studio scaffold every chapter for you.</p>
           <Button className="mt-4 rounded-full" onClick={()=>setOpen(true)}><Plus className="w-4 h-4 mr-2"/>Begin</Button>
         </div>
       ) : (
         <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
           {items.map(t => (
             <div key={t.id} className="card-crisp rounded-lg p-5 group hover:border-primary/60" data-testid={`thesis-card-${t.id}`}>
               <div className="flex justify-between">
                 <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center"><GraduationCap className="w-5 h-5"/></div>
                 <button onClick={()=>del(t.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4"/></button>
               </div>
               <div className="font-display text-xl mt-3 leading-tight line-clamp-2">{t.title}</div>
               <div className="text-xs text-muted-foreground mt-2">{t.template_key} · {t.university || "—"}</div>
               <div className="mt-4"><Link to={`/app/thesis/${t.id}`}><Button variant="outline" size="sm" className="rounded-full"><PenLine className="w-4 h-4 mr-1"/>Open</Button></Link></div>
             </div>
           ))}
         </div>
       )
      }
    </div>
  );
}
